import { supabase } from './supabase';
import { detectSupplierConflicts, type SupplierConflict } from './supplier-conflicts';
import { GIFT_PRODUCT_SELECT, buildProductSearchOr } from './product-search';
import type {
  Client,
  ClientContact,
  ContentBlock,
  DeliveryAddress,
  GiftPackage,
  GiftPackageItem,
  GiftProduct,
  GiftSupplier,
  PackageSection,
  PresentationExample,
  Project,
  PurchaseOrder,
} from './types';
export type { PackageSection } from './types';

// === CLIENT CONTACTS ===

export async function fetchClientContacts(clientId: string): Promise<ClientContact[]> {
  const { data, error } = await supabase
    .from('client_contacts')
    .select('id, client_id, name, email, phone, mobile, role, department, is_primary')
    .eq('client_id', clientId)
    .order('is_primary', { ascending: false })
    .order('name');
  if (error) throw error;
  return (data as ClientContact[]) ?? [];
}

export interface NewClientContact {
  client_id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  role?: string | null;
  is_primary?: boolean;
}

export async function createClientContact(input: NewClientContact): Promise<ClientContact> {
  const tenant_id = await fetchTenantId();
  const { data, error } = await supabase
    .from('client_contacts')
    .insert({
      tenant_id,
      client_id: input.client_id,
      name: input.name,
      email: input.email ?? null,
      phone: input.phone ?? null,
      mobile: input.mobile ?? null,
      role: input.role ?? null,
      is_primary: input.is_primary ?? false,
    })
    .select()
    .single();
  if (error) throw error;
  return data as ClientContact;
}

// === CLIENTS ===

export async function searchClients(query: string, limit = 20): Promise<Client[]> {
  let q = supabase
    .from('clients')
    .select('id, company_name, contact_name, contact_email')
    .order('company_name')
    .limit(limit);
  if (query) q = q.ilike('company_name', `%${query}%`);
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

// === PROJECTS ===

export async function fetchProjects(): Promise<(Project & { client?: Client | null })[]> {
  const { data, error } = await supabase
    .from('projects')
    .select('*, client:clients(id, company_name, contact_name, contact_email)')
    .eq('type', 'package')
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data as unknown as (Project & { client?: Client | null })[]) ?? [];
}

export async function fetchClient(id: string): Promise<Client | null> {
  const { data, error } = await supabase
    .from('clients')
    .select('id, company_name, contact_name, contact_email')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data as Client | null) ?? null;
}

export async function updateProjectClient(
  projectId: string,
  clientId: string | null
): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({ client_id: clientId })
    .eq('id', projectId);
  if (error) throw error;
}

export async function fetchProject(id: string): Promise<Project> {
  const { data, error } = await supabase.from('projects').select('*').eq('id', id).single();
  if (error) throw error;
  return data;
}

export async function updateProject(id: string, patch: Partial<Project>): Promise<void> {
  const { error } = await supabase.from('projects').update(patch).eq('id', id);
  if (error) throw error;
}

export interface NewProjectInput {
  client_id: string | null;
  client_contact_id?: string | null;
  name: string;
  description: string | null;
  deadline: string | null;
  package_count_planned: number | null;
}

export async function createProject(input: NewProjectInput): Promise<Project> {
  const { data, error } = await supabase
    .from('projects')
    .insert({ ...input, type: 'package', phase: 'concept', status: 'active' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteProject(id: string): Promise<void> {
  // Cascade-delete: packages → items + purchase_orders → po_items
  const { data: pkgs, error: pkgErr } = await supabase
    .from('gift_packages')
    .select('id')
    .eq('project_id', id);
  if (pkgErr) throw pkgErr;

  const pkgIds = (pkgs ?? []).map((p) => p.id);
  if (pkgIds.length > 0) {
    await supabase.from('gift_package_items').delete().in('package_id', pkgIds);

    const { data: pos } = await supabase
      .from('gift_purchase_orders')
      .select('id')
      .in('package_id', pkgIds);
    const poIds = (pos ?? []).map((p) => p.id);
    if (poIds.length > 0) {
      await supabase.from('gift_purchase_order_items').delete().in('purchase_order_id', poIds);
      await supabase.from('gift_purchase_orders').delete().in('id', poIds);
    }

    await supabase.from('gift_delivery_addresses').delete().in('package_id', pkgIds);
    await supabase.from('gift_packages').delete().in('id', pkgIds);
  }

  const { error } = await supabase.from('projects').delete().eq('id', id);
  if (error) throw error;
}

// === SUPPLIERS & PRODUCTS ===

const SUPPLIER_TERM_FIELDS =
  'id, name, min_order_value, transport_cost_below_min, order_lead_time_days, terms_text, terms_updated_at';

export async function fetchSuppliers(): Promise<GiftSupplier[]> {
  const { data, error } = await supabase
    .from('gift_suppliers')
    .select(SUPPLIER_TERM_FIELDS)
    .order('name');
  if (error) throw error;
  return (data as unknown as GiftSupplier[]) ?? [];
}

export async function updateSupplierTerms(
  id: string,
  patch: Pick<
    GiftSupplier,
    'min_order_value' | 'transport_cost_below_min' | 'order_lead_time_days' | 'terms_text'
  >
): Promise<void> {
  const { error } = await supabase
    .from('gift_suppliers')
    .update({ ...patch, terms_updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// Suppliers met >=1 actief product in de gegeven sectie.
// Gebruikt PostgREST inner-join zodat suppliers zonder match wegvallen.
export async function fetchSuppliersWithSection(
  section: PackageSection | 'alles'
): Promise<GiftSupplier[]> {
  if (section === 'alles') return fetchSuppliers();

  let q = supabase
    .from('gift_suppliers')
    .select('id, name, gift_products!inner(id, active, product_type, is_packaging)')
    .eq('gift_products.active', true)
    .order('name');

  switch (section) {
    case 'verpakking':
      q = q.eq('gift_products.is_packaging', true);
      break;
    case 'gadget':
      q = q.eq('gift_products.is_packaging', false).eq('gift_products.product_type', 'non_food');
      break;
    case 'drank':
      q = q.eq('gift_products.is_packaging', false).eq('gift_products.product_type', 'alcohol');
      break;
    case 'food':
      q = q.eq('gift_products.is_packaging', false).eq('gift_products.product_type', 'food');
      break;
    case 'overig':
      break;
  }

  const { data, error } = await q;
  if (error) throw error;
  const seen = new Set<string>();
  const result: GiftSupplier[] = [];
  for (const row of (data ?? []) as Array<{ id: string; name: string }>) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    result.push({ id: row.id, name: row.name });
  }
  return result;
}

export async function searchProducts(
  query: string,
  supplierId?: string,
  limit = 50
): Promise<GiftProduct[]> {
  let q = supabase
    .from('gift_products')
    .select(GIFT_PRODUCT_SELECT)
    .eq('active', true)
    .order('name')
    .limit(limit);
  if (supplierId) q = q.eq('supplier_id', supplierId);
  if (query) {
    q = q.or(buildProductSearchOr(query));
  }
  const { data, error } = await q;
  if (error) throw error;
  return (data as unknown as GiftProduct[]) ?? [];
}

// === GIFT PACKAGES ===

export async function fetchPackagesByProject(projectId: string): Promise<GiftPackage[]> {
  const { data, error } = await supabase
    .from('gift_packages')
    .select(
      '*, items:gift_package_items(*, gift_product:gift_products(*, supplier:gift_suppliers(id, name)))'
    )
    .eq('project_id', projectId)
    .order('created_at');
  if (error) throw error;
  const pkgs = (data as unknown as GiftPackage[]) ?? [];
  // Pas per-item unit_purchase_price_override toe op product.unit_purchase_price
  // zodat alle downstream calc-functies (VPELines, margin, recipe) automatisch
  // de overschreven prijs gebruiken. Origineel blijft in DB onaangetast.
  for (const pkg of pkgs) {
    for (const it of pkg.items ?? []) {
      if (it.unit_purchase_price_override != null && it.gift_product) {
        it.gift_product = {
          ...it.gift_product,
          unit_purchase_price: it.unit_purchase_price_override,
        };
      }
    }
    // Items op sort_order zetten (nulls achteraan, stabiel) zodat drag-and-drop volgorde
    // consistent terugkomt. Nieuwe items zonder sort_order vallen achteraan.
    if (pkg.items) {
      pkg.items.sort((a, b) => {
        const ao = a.sort_order ?? Number.MAX_SAFE_INTEGER;
        const bo = b.sort_order ?? Number.MAX_SAFE_INTEGER;
        return ao - bo;
      });
    }
  }
  return pkgs;
}

/**
 * Eén pakket met items + product/leverancier-data, voor de read-only vergelijk-view
 * (S2). Zelfde override-merge + sort als fetchPackagesByProject.
 */
export async function fetchPackageWithItems(packageId: string): Promise<GiftPackage | null> {
  const { data, error } = await supabase
    .from('gift_packages')
    .select(
      '*, items:gift_package_items(*, gift_product:gift_products(*, supplier:gift_suppliers(id, name)))'
    )
    .eq('id', packageId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const pkg = data as unknown as GiftPackage;
  for (const it of pkg.items ?? []) {
    if (it.unit_purchase_price_override != null && it.gift_product) {
      it.gift_product = {
        ...it.gift_product,
        unit_purchase_price: it.unit_purchase_price_override,
      };
    }
  }
  pkg.items?.sort((a, b) => {
    const ao = a.sort_order ?? Number.MAX_SAFE_INTEGER;
    const bo = b.sort_order ?? Number.MAX_SAFE_INTEGER;
    return ao - bo;
  });
  return pkg;
}

export interface NewPackageInput {
  project_id: string;
  client_id: string | null;
  name: string;
  quantity: number;
  budget_per_package: number;
  budget_type: 'incl_btw' | 'excl_btw';
  variant_label?: string;
  variant_status?: 'concept' | 'alternatief' | 'definitief';
  season?: string;
  /** Alleen gebruiken bij een bewuste kopie; zonder override start een nieuw pakket op v2. */
  pricing?: {
    handling_cost_per_package: number;
    transport_cost_per_package: number;
    pricing_model_version: GiftPackage['pricing_model_version'];
    margin_pct: number;
    margin_low_pct: number;
    margin_high_pct: number;
    handling_margin_pct: number | null;
    transport_margin_pct: number | null;
  };
}

export async function createPackage(input: NewPackageInput): Promise<GiftPackage> {
  const { variant_status, variant_label, season, pricing, ...rest } = input;
  const pricingValues = pricing ?? {
    handling_cost_per_package: 5,
    transport_cost_per_package: 0,
    pricing_model_version: 'component_margin_v2' as const,
    margin_pct: 30,
    margin_low_pct: 30,
    margin_high_pct: 30,
    handling_margin_pct: 30,
    transport_margin_pct: 30,
  };
  const { data, error } = await supabase
    .from('gift_packages')
    .insert({
      ...rest,
      season: season ?? 'kerst',
      ...pricingValues,
      variant_label: variant_label ?? 'Standaard',
      variant_status: variant_status ?? 'concept',
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export function packagePricingOverrideFromPackage(
  pkg: GiftPackage
): NonNullable<NewPackageInput['pricing']> {
  return {
    handling_cost_per_package: pkg.handling_cost_per_package,
    transport_cost_per_package: pkg.transport_cost_per_package,
    pricing_model_version: pkg.pricing_model_version ?? 'legacy_v1',
    margin_pct: pkg.margin_pct,
    margin_low_pct: pkg.margin_low_pct ?? pkg.margin_pct ?? 30,
    margin_high_pct: pkg.margin_high_pct ?? pkg.margin_pct ?? 30,
    handling_margin_pct: pkg.handling_margin_pct,
    transport_margin_pct: pkg.transport_margin_pct,
  };
}

export async function copyPackageItems(fromPackageId: string, toPackageId: string): Promise<void> {
  // Gebruik SELECT * zodat we niet falen op kolommen die niet bestaan in de huidige
  // schema-versie (notes/excluded/etc. zijn pas in latere migraties toegevoegd).
  // SELECT * neemt ook sort_order + section_override + overrides mee voor een getrouwe kloon.
  const { data: items, error } = await supabase
    .from('gift_package_items')
    .select('*')
    .eq('package_id', fromPackageId);
  if (error) {
    console.error('[copyPackageItems] select failed:', error);
    throw new Error(`Items van bron-pakket ophalen mislukt: ${error.message}`);
  }
  if (!items || items.length === 0) {
    console.warn('[copyPackageItems] bron-pakket heeft geen items:', fromPackageId);
    return;
  }
  // Strip kolommen die door Postgres zelf gezet worden of die we niet willen kopiëren.
  const rows = items.map((it) => {
    const row = { ...(it as Record<string, unknown>) };
    delete row.id;
    delete row.created_at;
    delete row.updated_at;
    row.package_id = toPackageId;
    return row;
  });
  const { error: insErr, data: inserted } = await supabase
    .from('gift_package_items')
    .insert(rows)
    .select('id');
  if (insErr) {
    console.error('[copyPackageItems] insert failed:', insErr, 'rows:', rows);
    throw new Error(`Items kopiëren mislukt: ${insErr.message}`);
  }
  console.info(`[copyPackageItems] ${inserted?.length ?? 0} items gekopieerd naar ${toPackageId}`);
}

export interface ClonablePackage {
  id: string;
  name: string;
  variant_label: string | null;
  variant_status: string | null;
  quantity: number;
  project_id: string;
  project_name: string;
  client_name: string | null;
  item_count: number;
}

/**
 * Pakketten (varianten) die als kloon-bron kunnen dienen in de wizard: bestaande
 * configuraties met items, gegroepeerd per project. Sluit het huidige project uit.
 */
export async function fetchClonablePackages(excludeProjectId?: string): Promise<ClonablePackage[]> {
  const { data, error } = await supabase
    .from('gift_packages')
    .select(
      // FK expliciet: projects heeft twee relaties met gift_packages (project_id én gift_package_id),
      // dus PostgREST kan de embed niet automatisch oplossen (PGRST201) zonder de FK-naam.
      'id, name, variant_label, variant_status, quantity, project_id, project:projects!gift_packages_project_id_fkey(name, client:clients(company_name)), items:gift_package_items(id)'
    )
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw error;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data as any[]) ?? [])
    .map((p) => ({
      id: p.id,
      name: p.name,
      variant_label: p.variant_label,
      variant_status: p.variant_status,
      quantity: p.quantity,
      project_id: p.project_id,
      project_name: p.project?.name ?? 'Onbekend project',
      client_name: p.project?.client?.company_name ?? null,
      item_count: Array.isArray(p.items) ? p.items.length : 0,
    }))
    .filter((p) => p.item_count > 0 && p.project_id !== excludeProjectId);
}

type MutableGiftPackageFields = Omit<GiftPackage, 'id' | 'pricing_model_version' | 'created_at'>;

export async function updatePackage(
  id: string,
  patch: Partial<MutableGiftPackageFields>
): Promise<GiftPackage> {
  const { data, error } = await supabase
    .from('gift_packages')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// === DELIVERY ADDRESSES ===

export async function fetchDeliveryAddresses(packageId: string): Promise<DeliveryAddress[]> {
  const { data, error } = await supabase
    .from('gift_delivery_addresses')
    .select('*')
    .eq('package_id', packageId)
    .order('created_at');
  if (error) throw error;
  return (data as DeliveryAddress[]) ?? [];
}

let cachedTenantId: string | null = null;

export async function fetchTenantId(): Promise<string> {
  if (cachedTenantId) return cachedTenantId;
  const { data, error } = await supabase.rpc('get_user_tenant_id');
  if (error || !data) throw error ?? new Error('tenant_id niet beschikbaar');
  cachedTenantId = data as string;
  return cachedTenantId;
}

export interface ClientAddress {
  street: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  contact_name: string | null;
  contact_phone: string | null;
}

export async function fetchClientAddress(clientId: string): Promise<ClientAddress | null> {
  const { data, error } = await supabase
    .from('clients')
    .select('street, postal_code, city, country, contact_name, contact_phone')
    .eq('id', clientId)
    .maybeSingle();
  if (error) throw error;
  return (data as ClientAddress | null) ?? null;
}

export async function createDeliveryAddress(
  input: Omit<DeliveryAddress, 'id'> & { tenant_id?: string }
): Promise<DeliveryAddress> {
  const tenant_id = input.tenant_id ?? (await fetchTenantId());
  const { data, error } = await supabase
    .from('gift_delivery_addresses')
    .insert({ ...input, tenant_id })
    .select()
    .single();
  if (error) throw error;
  return data as DeliveryAddress;
}

export async function updateDeliveryAddress(
  id: string,
  patch: Partial<DeliveryAddress>
): Promise<void> {
  const { error } = await supabase.from('gift_delivery_addresses').update(patch).eq('id', id);
  if (error) throw error;
}

export async function deleteDeliveryAddress(id: string): Promise<void> {
  const { error } = await supabase.from('gift_delivery_addresses').delete().eq('id', id);
  if (error) throw error;
}

// === PURCHASE ORDERS ===

export async function fetchPurchaseOrders(packageId: string): Promise<PurchaseOrder[]> {
  const { data, error } = await supabase
    .from('gift_purchase_orders')
    .select(
      `*, supplier:gift_suppliers(${SUPPLIER_TERM_FIELDS}), items:gift_purchase_order_items(*, gift_product:gift_products(id, name, brand, vpe, unit_purchase_price, min_order_qty, available_from))`
    )
    .eq('package_id', packageId)
    .order('created_at');
  if (error) throw error;
  return (data as unknown as PurchaseOrder[]) ?? [];
}

interface VpeLineLike {
  product: { id: string; supplier_id: string | null; vpe: number; unit_purchase_price: number };
  vpe_units_needed: number;
  total_ordered: number;
  leftover_units: number;
  unit_purchase_price: number;
}

/**
 * Genereert per leverancier 1 PO met regels (op basis van vpeLines).
 * Wist eerste bestaande draft-PO's voor dit pakket, behoudt bevestigd/geleverd.
 */
export async function generatePurchaseOrdersForPackage(
  packageId: string,
  vpeLines: VpeLineLike[]
): Promise<PurchaseOrder[]> {
  // Verzamel per supplier
  const grouped = new Map<string, VpeLineLike[]>();
  for (const line of vpeLines) {
    const sid = line.product.supplier_id;
    if (!sid) continue;
    if (!grouped.has(sid)) grouped.set(sid, []);
    grouped.get(sid)!.push(line);
  }

  // Verwijder bestaande draft-PO's (status=draft) voor dit pakket
  await supabase
    .from('gift_purchase_orders')
    .delete()
    .eq('package_id', packageId)
    .eq('status', 'draft');

  const created: PurchaseOrder[] = [];
  for (const [supplierId, lines] of grouped) {
    const { data: po, error: poErr } = await supabase
      .from('gift_purchase_orders')
      .insert({
        package_id: packageId,
        supplier_id: supplierId,
        status: 'draft',
      })
      .select()
      .single();
    if (poErr) throw poErr;

    const itemRows = lines.map((l) => ({
      purchase_order_id: po.id,
      gift_product_id: l.product.id,
      vpe_units_ordered: l.vpe_units_needed,
      total_units_ordered: l.total_ordered,
      quoted_unit_price: l.unit_purchase_price,
      leftover_units: l.leftover_units,
    }));
    const { error: iErr } = await supabase.from('gift_purchase_order_items').insert(itemRows);
    if (iErr) throw iErr;
    created.push(po as PurchaseOrder);
  }

  return created;
}

export async function updatePurchaseOrderStatus(
  id: string,
  status: PurchaseOrder['status'],
  invoiceRef?: string | null
): Promise<void> {
  const patch: Partial<PurchaseOrder> = { status };
  if (invoiceRef !== undefined) patch.supplier_invoice_ref = invoiceRef;
  const { error } = await supabase.from('gift_purchase_orders').update(patch).eq('id', id);
  if (error) throw error;
  // Levering (status 'delivered') + restvoorraad lopen nu via de record_delivery-RPC
  // (lib/api/rounds.ts), niet meer via deze functie. De oude writeback-naar-status is
  // verwijderd; de deprecated gift_inventory.status-kolom wordt door 072b gedropt.
}

export async function updatePurchaseOrder(
  id: string,
  patch: Partial<PurchaseOrder>
): Promise<void> {
  const { error } = await supabase.from('gift_purchase_orders').update(patch).eq('id', id);
  if (error) throw error;
}

/**
 * Upload PO-bevestiging of pakbon naar bucket gift_purchase_docs.
 * Path-vorm: `{purchaseOrderId}/{kind}-{timestamp}-{filename}`.
 * Bucket is privé, dus we slaan signed URL niet op maar het path zelf zodat
 * we later opnieuw een korte signed URL kunnen genereren.
 */
export async function uploadPurchaseDoc(
  purchaseOrderId: string,
  kind: 'po' | 'delivery',
  file: File
): Promise<string> {
  const safeName = file.name.replace(/[^\w.\-]+/g, '_');
  const path = `${purchaseOrderId}/${kind}-${Date.now()}-${safeName}`;
  const { error } = await supabase.storage
    .from('gift_purchase_docs')
    .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false });
  if (error) throw error;
  return path;
}

/** Geeft een tijdelijke signed URL (default 1 uur) voor een opgeslagen PO-doc. */
export async function getPurchaseDocSignedUrl(path: string, expiresInSec = 3600): Promise<string> {
  const { data, error } = await supabase.storage
    .from('gift_purchase_docs')
    .createSignedUrl(path, expiresInSec);
  if (error) throw error;
  return data.signedUrl;
}

export async function updatePurchaseOrderItemActualPrice(
  itemId: string,
  actualPrice: number | null
): Promise<void> {
  const { error } = await supabase
    .from('gift_purchase_order_items')
    .update({ actual_invoice_price: actualPrice })
    .eq('id', itemId);
  if (error) throw error;
}

export interface OpenPurchaseOrder extends PurchaseOrder {
  package?: {
    id: string;
    name: string;
    project_id: string | null;
    handling_date?: string | null;
  } | null;
  project?: { id: string; name: string } | null;
}

export async function fetchAllOpenPurchaseOrders(): Promise<OpenPurchaseOrder[]> {
  const { data, error } = await supabase
    .from('gift_purchase_orders')
    .select(
      `*, supplier:gift_suppliers(${SUPPLIER_TERM_FIELDS}), package:gift_packages(id, name, project_id, handling_date), items:gift_purchase_order_items(*, gift_product:gift_products(id, name, brand, vpe, unit_purchase_price, min_order_qty, available_from))`
    )
    .in('status', ['draft', 'ordered', 'confirmed'])
    .order('created_at', { ascending: false });
  if (error) throw error;

  const orders = (data as unknown as OpenPurchaseOrder[]) ?? [];
  const projectIds = Array.from(
    new Set(orders.map((o) => o.package?.project_id).filter((p): p is string => Boolean(p)))
  );
  if (projectIds.length > 0) {
    const { data: projects } = await supabase
      .from('projects')
      .select('id, name')
      .in('id', projectIds);
    const byId = new Map((projects ?? []).map((p) => [p.id, p]));
    for (const o of orders) {
      const pid = o.package?.project_id;
      if (pid) o.project = byId.get(pid) ?? null;
    }
  }
  return orders;
}

/**
 * Conflict-check voor één purchase order tegen de leveranciersvoorwaarden.
 * Pure mapping naar de engine in supplier-conflicts.ts.
 */
export function purchaseOrderConflicts(
  po: PurchaseOrder,
  handlingDate: string | null | undefined
): SupplierConflict[] {
  const supplier = po.supplier;
  if (!supplier) return [];
  const items = (po.items ?? []).map((it) => ({
    name: it.gift_product?.name ?? 'Onbekend product',
    total_ordered: it.total_units_ordered,
    min_order_qty: it.gift_product?.min_order_qty ?? null,
    available_from: it.gift_product?.available_from ?? null,
  }));
  const purchaseTotalExclBtw = (po.items ?? []).reduce(
    (s, it) => s + it.quoted_unit_price * it.total_units_ordered,
    0
  );
  return detectSupplierConflicts({
    supplierName: supplier.name,
    terms: {
      min_order_value: supplier.min_order_value,
      transport_cost_below_min: supplier.transport_cost_below_min,
      order_lead_time_days: supplier.order_lead_time_days,
    },
    purchaseTotalExclBtw,
    items,
    handlingDate: handlingDate ?? null,
  });
}

export interface AttentionPoint {
  conflict: SupplierConflict;
  projectId: string | null;
  projectName: string;
  packageName: string;
}

/** Alle conflict-aandachtspunten over de open inkoop-orders heen (voor de bel). */
export async function fetchAttentionPoints(): Promise<AttentionPoint[]> {
  const orders = await fetchAllOpenPurchaseOrders();
  const points: AttentionPoint[] = [];
  for (const o of orders) {
    for (const c of purchaseOrderConflicts(o, o.package?.handling_date)) {
      points.push({
        conflict: c,
        projectId: o.package?.project_id ?? null,
        projectName: o.project?.name ?? 'Onbekend project',
        packageName: o.package?.name ?? '',
      });
    }
  }
  return points;
}

// === PACKAGE ITEMS ===

export interface NewItemInput {
  package_id: string;
  gift_product_id: string;
  quantity_per_package: number;
  selling_price_per_item: number;
}

export async function addPackageItem(input: NewItemInput): Promise<GiftPackageItem> {
  const { data, error } = await supabase.from('gift_package_items').insert(input).select().single();
  if (error) throw error;
  return data;
}

export async function removePackageItem(id: string): Promise<void> {
  const { error } = await supabase.from('gift_package_items').delete().eq('id', id);
  if (error) throw error;
}

export async function updatePackageItem(
  id: string,
  patch: Partial<GiftPackageItem>
): Promise<GiftPackageItem> {
  const { data, error } = await supabase
    .from('gift_package_items')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Persisteer een drag-and-drop herordening: per item de nieuwe sort_order en
 * section_override (NULL als de visuele sectie gelijk is aan de natuurlijke).
 */
export async function reorderPackageItems(
  updates: { id: string; sort_order: number; section_override: PackageSection | null }[]
): Promise<void> {
  await Promise.all(
    updates.map((u) =>
      supabase
        .from('gift_package_items')
        .update({ sort_order: u.sort_order, section_override: u.section_override })
        .eq('id', u.id)
    )
  );
}

// === CONTEXT: vorig pakket bij klant + favorieten over alle Mark's pakketten ===

export interface ContextProduct {
  product: GiftProduct;
  count: number;
}

export async function fetchClientHistory(
  clientId: string,
  excludePackageId?: string
): Promise<ContextProduct[]> {
  const { data: pkgs, error: pErr } = await supabase
    .from('gift_packages')
    .select('id, created_at')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false })
    .limit(5);
  if (pErr) throw pErr;
  const pkgIds = (pkgs ?? [])
    .filter((p) => p.id !== excludePackageId)
    .map((p) => p.id)
    .slice(0, 3);
  if (pkgIds.length === 0) return [];

  const { data: items, error: iErr } = await supabase
    .from('gift_package_items')
    .select(
      `gift_product_id, quantity_per_package, gift_product:gift_products(${GIFT_PRODUCT_SELECT})`
    )
    .in('package_id', pkgIds);
  if (iErr) throw iErr;

  const byProduct = new Map<string, ContextProduct>();
  for (const item of items ?? []) {
    const raw = item.gift_product as unknown;
    const product = (Array.isArray(raw) ? raw[0] : raw) as GiftProduct | undefined;
    if (!product) continue;
    const existing = byProduct.get(product.id);
    if (existing) {
      existing.count += 1;
    } else {
      byProduct.set(product.id, { product, count: 1 });
    }
  }
  return Array.from(byProduct.values()).sort((a, b) => b.count - a.count);
}

export async function fetchFavorites(limit = 8): Promise<ContextProduct[]> {
  const { data: items, error } = await supabase
    .from('gift_package_items')
    .select(`gift_product_id, gift_product:gift_products(${GIFT_PRODUCT_SELECT})`)
    .limit(800);
  if (error) throw error;

  const byProduct = new Map<string, ContextProduct>();
  for (const item of items ?? []) {
    const raw = item.gift_product as unknown;
    const product = (Array.isArray(raw) ? raw[0] : raw) as GiftProduct | undefined;
    if (!product || product.active === false) continue;
    const existing = byProduct.get(product.id);
    if (existing) {
      existing.count += 1;
    } else {
      byProduct.set(product.id, { product, count: 1 });
    }
  }
  return Array.from(byProduct.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

// === SECTION FILTERS voor catalogus + builder ===

// PackageSection wordt vanuit types.ts geherexporteerd (zie boven).

export const SECTION_LABEL: Record<PackageSection, string> = {
  verpakking: 'Verpakking',
  gadget: 'Gadgets',
  drank: 'Drank',
  food: 'Food',
  overig: 'Overig',
};

export function sectionOf(
  product: Pick<GiftProduct, 'product_type' | 'is_packaging'>
): PackageSection {
  if (product.is_packaging) return 'verpakking';
  if (product.product_type === 'alcohol') return 'drank';
  if (product.product_type === 'food') return 'food';
  if (product.product_type === 'non_food') return 'gadget';
  return 'overig';
}

/**
 * Effectieve sectie van een pakket-item: visuele override (drag tussen secties) wint,
 * anders afgeleid uit het product via sectionOf().
 */
export function effectiveSection(
  item: Pick<GiftPackageItem, 'section_override' | 'gift_product'>
): PackageSection {
  if (item.section_override) return item.section_override;
  return item.gift_product ? sectionOf(item.gift_product) : 'overig';
}

export async function searchProductsBySection(
  section: PackageSection | 'alles',
  query: string,
  supplierId?: string,
  limit = 50
): Promise<GiftProduct[]> {
  let q = supabase
    .from('gift_products')
    .select(GIFT_PRODUCT_SELECT)
    .eq('active', true)
    .order('name')
    .limit(limit);

  switch (section) {
    case 'verpakking':
      q = q.eq('is_packaging', true);
      break;
    case 'gadget':
      q = q.eq('is_packaging', false).eq('product_type', 'non_food');
      break;
    case 'drank':
      q = q.eq('is_packaging', false).eq('product_type', 'alcohol');
      break;
    case 'food':
      q = q.eq('is_packaging', false).eq('product_type', 'food');
      break;
    case 'overig':
    case 'alles':
      break;
  }

  if (supplierId) q = q.eq('supplier_id', supplierId);
  if (query) {
    q = q.or(buildProductSearchOr(query));
  }
  const { data, error } = await q;
  if (error) throw error;
  return (data as unknown as GiftProduct[]) ?? [];
}

export async function updateProduct(
  id: string,
  patch: {
    name?: string;
    brand?: string | null;
    unit_purchase_price?: number;
    vpe?: number;
    btw_category?: 'low' | 'high';
    product_type?: string;
    is_packaging?: boolean;
  }
): Promise<void> {
  const { error } = await supabase.from('gift_products').update(patch).eq('id', id);
  if (error) throw error;
}

export async function deletePackage(id: string): Promise<void> {
  const { error } = await supabase.from('gift_packages').delete().eq('id', id);
  if (error) throw error;
}

// === OFFERTETEKSTEN: blokkenbibliotheek + huisstijl-voorbeelden ===

export async function fetchContentBlocks(kind?: ContentBlock['kind']): Promise<ContentBlock[]> {
  let q = supabase
    .from('content_blocks')
    .select('id, kind, title, body, tags, active, created_at')
    .eq('active', true)
    .order('kind')
    .order('title');
  if (kind) q = q.eq('kind', kind);
  const { data, error } = await q;
  if (error) throw error;
  return (data as unknown as ContentBlock[]) ?? [];
}

export async function saveContentBlock(input: {
  id?: string;
  kind: ContentBlock['kind'];
  title: string;
  body: string;
  tags?: string[];
  active?: boolean;
}): Promise<ContentBlock> {
  if (input.id) {
    const { data, error } = await supabase
      .from('content_blocks')
      .update({
        kind: input.kind,
        title: input.title,
        body: input.body,
        tags: input.tags ?? null,
        active: input.active ?? true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', input.id)
      .select('id, kind, title, body, tags, active, created_at')
      .single();
    if (error) throw error;
    return data as unknown as ContentBlock;
  }
  const tenant_id = await fetchTenantId();
  const { data, error } = await supabase
    .from('content_blocks')
    .insert({
      tenant_id,
      kind: input.kind,
      title: input.title,
      body: input.body,
      tags: input.tags ?? null,
      active: input.active ?? true,
    })
    .select('id, kind, title, body, tags, active, created_at')
    .single();
  if (error) throw error;
  return data as unknown as ContentBlock;
}

export async function deleteContentBlock(id: string): Promise<void> {
  const { error } = await supabase.from('content_blocks').delete().eq('id', id);
  if (error) throw error;
}

export async function fetchPresentationExamples(): Promise<PresentationExample[]> {
  const { data, error } = await supabase
    .from('presentation_examples')
    .select('id, title, body, theme, source, created_at')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as unknown as PresentationExample[]) ?? [];
}

export async function savePresentationExample(input: {
  title: string;
  body: string;
  theme?: string;
  source?: string;
}): Promise<PresentationExample> {
  const tenant_id = await fetchTenantId();
  const { data, error } = await supabase
    .from('presentation_examples')
    .insert({
      tenant_id,
      title: input.title,
      body: input.body,
      theme: input.theme ?? null,
      source: input.source ?? null,
    })
    .select('id, title, body, theme, source, created_at')
    .single();
  if (error) throw error;
  return data as unknown as PresentationExample;
}

export async function deletePresentationExample(id: string): Promise<void> {
  const { error } = await supabase.from('presentation_examples').delete().eq('id', id);
  if (error) throw error;
}

// --- Additive api-split (story 1.5): nieuwe I/O ge-barreld ---
export * from './api/inventory';
export * from './api/projects';
export * from './api/confirmations';
export * from './api/rounds';
